import React, { useEffect, useState } from "react";
import { Col } from "react-bootstrap";
import {
  BackButton,
  Box,
  ButtonStyled,
  LazyImage,
  SpanStyled,
  TextStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import {
  GreenSuccessCheck,
  PlaceholderImg,
  RedCancelIcon,
} from "../../../components/styled/AllImages";
import { useTranslation } from "react-i18next";
import { ContentWrapper } from "../../../components/styled/styled";
import {
  tempTUserDtlPermissionColumns,
  userDtlPermissionColumns,
} from "../../../helpers/adminConstants/TableConstants";
import {
  tempUserPermissionsRows,
  userPermissionsRows,
} from "../../../helpers/dummyConstants";
import { UserDetailsWrapper } from "./styled";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { StyledTable } from "./components/UserPermissions";
import UsersApi from "../../../services/api/usersApi";
import { useUser } from "../../../context/userContext";
import { getModuleOperations } from "../../../utils";

const assetURL = process.env.REACT_APP_ASSET_URL;

const TempUserDetails = () => {
  const permissionsObj = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
  };

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [userDetails, setUserDetails] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [permissions, setPermissions] = useState(permissionsObj);
  const [sharedCabinets, setSharedCabinets] = useState([]);

  const getTempSingleUser = async () => {
    try {
      setDataLoading(true);
      const { data, code } = await UsersApi.getTempSingleUser(id);
      if (code === 200) {
        const profileImg = data?.profile
          ? `${assetURL}/${data?.profile}`
          : PlaceholderImg;

        setUserDetails({
          ...data,
          user_id: data?.id,
          profile: profileImg,
          status: parseInt(data?.status),
          name: data?.name || "",
          id_number: data?.user_detail?.id_number || "",
          email: data?.email || "",
          phone: data?.phone || "",
        });

        const mappedPermissions = {};

        data?.shared_cabinets?.forEach(({ cabinet_id, operation_id }) => {
          if (!mappedPermissions[cabinet_id]) {
            mappedPermissions[cabinet_id] = new Set();
          }
          mappedPermissions[cabinet_id].add(operation_id);
        });

        for (let i = 1; i <= 6; i++) {
          if (!mappedPermissions[i]) {
            mappedPermissions[i] = new Set();
          }
        }

        const finalPermissions = {};
        Object.keys(mappedPermissions).forEach((cabinetId) => {
          finalPermissions[cabinetId.toString()] = Array.from(
            mappedPermissions[cabinetId]
          );
        });

        setPermissions(finalPermissions);
      }
    } catch (error) {
    } finally {
      setDataLoading(false);
    }
  };

  const getSharedCabinets = async () => {
    try {
      const { code, data } = await UsersApi.getSharedCabinets();
      if (code === 200) {
        const updatedData = data?.data?.map((cabinet) => ({
          id: cabinet?.id,
          module_name: cabinet?.name,
          view: 1,
          view_open: 2,
        }));
        setSharedCabinets(updatedData);
      }
    } catch (err) {
      console.error("Failed to fetch shared cabinets", err);
    }
  };

  useEffect(() => {
    getTempSingleUser();
    getSharedCabinets();
  }, []);

  return (
    <UserDetailsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("user_details")}
            onClick={() => navigate("/temporary-users")}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <ContentWrapper>
        {dataLoading ? (
          <ThemeLoader />
        ) : (
          <Box className="d-flex flex-wrap">
            <Col md={4} sm={12} className="user-info-box col-12">
              <Box className="d-flex align-items-center justify-content-between gap-1">
                <Box className="d-flex align-items-center gap-2">
                  <LazyImage
                    src={userDetails?.profile}
                    alt="User avatar"
                    className="rounded-circle"
                    style={{ width: "48px", height: "48px" }}
                  />
                  <Box>
                    <TextStyled size="20px" weight="900" className="mb-0">
                      {userDetails?.name}
                    </TextStyled>
                    <SpanStyled
                      size="16px"
                      weight="400"
                      color="var(--text2)"
                      className="mb-0"
                    >
                      {userDetails?.id_number}
                    </SpanStyled>
                  </Box>
                </Box>
                {user && Number(user?.user_type_id) !== 4 && (
                  <ButtonStyled
                    label={t("edit_user")}
                    onClick={() =>
                      navigate(`/temporary-users/edit-temporary-user/${id}`)
                    }
                  />
                )}
              </Box>
              <Box className="d-flex align-items-center gap-2">
                <Col sm={4} className="col-4">
                  <SpanStyled color="var(--text2)">{t("status")}</SpanStyled>
                </Col>
                <Col>
                  <SpanStyled
                    color={
                      userDetails?.status === 1 || userDetails?.is_expired != 1
                        ? "var(--themeColor2)"
                        : "var(--redColor)"
                    }
                  >
                    {t(
                      userDetails.status === 1
                        ? "active"
                        : userDetails?.is_expired == 1
                        ? "expired"
                        : "deactive"
                    )}
                  </SpanStyled>
                </Col>
              </Box>

              {[
                { label: "email", value: userDetails?.email },
                { label: "phone", value: userDetails?.phone },
              ].map((field, index) => (
                <Box key={index} className="d-flex gap-2 align-items-center">
                  <Col sm={4} className="col-4">
                    <SpanStyled color="var(--text2)">
                      {t(field.label)}
                    </SpanStyled>
                  </Col>
                  <Col className="overflow-hidden">
                    <Box style={{ direction: "ltr", ...(field?.label === "email" && {wordWrap: "break-word"}) }}>{t(field.value)}</Box>
                  </Col>
                </Box>
              ))}
            </Col>

            <Col md={8} sm={12} className="p-0 col-12 overflow-x-auto">
              <StyledTable className="border-0">
                <thead>
                  <tr>
                    {tempTUserDtlPermissionColumns?.map((col, index) => (
                      <th
                        key={index}
                        className={`${col?.title === "" && "title-column"}`}
                      >
                        <SpanStyled
                          size="16px"
                          color="var(--text)"
                          weight="bold"
                        >
                          {t(col.title)}
                        </SpanStyled>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sharedCabinets?.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {tempTUserDtlPermissionColumns?.map((col, colIndex) => {
                        const ops = permissions?.[row.id?.toString()] || [];
                        let opId = null;
                        if (col.name === "view_only") opId = 1;
                        else if (col.name === "view_and_open") opId = 2;

                        return (
                          <td key={colIndex}>
                            <Box className="d-flex justify-content-center">
                              {colIndex === 0 ? (
                                <SpanStyled size="16px" color="var(--text2)">
                                  {t(row[col.name])}
                                </SpanStyled>
                              ) : ops?.includes(opId) ? (
                                <GreenSuccessCheck />
                              ) : (
                                <RedCancelIcon />
                              )}
                            </Box>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </Col>
          </Box>
        )}
      </ContentWrapper>
    </UserDetailsWrapper>
  );
};

export default TempUserDetails;
